<template>
  <b-card no-body class="mb-2">
    <b-card-header class="d-flex align-items-center py-1" style="border-bottom:1px solid #ebe9f1;">
      <feather-icon icon="ClockIcon" size="16" class="mr-75 text-primary" />
      <span class="font-weight-bold" style="font-size:0.95rem;">Hourly Call Activity</span>
      <b-badge variant="light-primary" class="ml-75">{{ hourlyAnalytics.length }} hours with data</b-badge>
      <span class="ml-auto text-muted" style="font-size:0.78rem;">
        <feather-icon icon="SunIcon" size="12" class="mr-25 text-warning" />Working hours: 09:30 – 18:30
      </span>
    </b-card-header>

    <div class="hourly-table-scroll">
      <table class="table table-hover table-sm mb-0" style="font-size:0.82rem;">
        <thead style="background:#f8f8f8;">
          <tr>
            <th class="px-1 py-75 text-muted font-weight-bold" style="white-space:nowrap; min-width:140px;">Hour</th>
            <th class="px-1 py-75 text-center text-muted font-weight-bold">Total</th>
            <th class="px-1 py-75 text-center font-weight-bold" style="color:#28c76f;">Connected</th>
            <th class="px-1 py-75 text-muted font-weight-bold" style="min-width:160px;">Connection Rate</th>
            <th class="px-1 py-75 text-center font-weight-bold" style="color:#ff9f43;">Not Ans.</th>
            <th class="px-1 py-75 text-center font-weight-bold" style="color:#00cfe8;">Missed</th>
            <th class="px-1 py-75 text-muted font-weight-bold" style="white-space:nowrap;">Avg Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="r in enrichedRows"
            :key="r.hour"
            :class="rowClass(r)"
          >
            <!-- Hour label -->
            <td class="px-1 py-75 align-middle">
              <div class="d-flex align-items-center" style="gap:6px;">
                <feather-icon
                  :icon="isWorkingHour(r.hour) ? 'SunIcon' : 'MoonIcon'"
                  size="11"
                  :class="isWorkingHour(r.hour) ? 'text-warning' : 'text-secondary'"
                  style="opacity:0.6; flex-shrink:0;"
                />
                <span class="font-weight-bold text-nowrap" :class="r.total === 0 ? 'text-muted' : ''">{{ r.label }}</span>
                <b-badge
                  v-if="r.isPeak"
                  variant="light-warning"
                  style="font-size:0.65rem; padding:2px 5px;"
                >Peak</b-badge>
              </div>
            </td>

            <!-- Total -->
            <td class="px-1 py-75 text-center align-middle">
              <span :class="r.total === 0 ? 'text-muted' : 'font-weight-bold'">{{ r.total }}</span>
            </td>

            <!-- Connected -->
            <td class="px-1 py-75 text-center align-middle">
              <span class="font-weight-bold text-success">{{ r.connected }}</span>
            </td>

            <!-- Connection Rate -->
            <td class="px-1 py-75 align-middle">
              <div v-if="r.total > 0" class="d-flex align-items-center" style="gap:8px;">
                <div class="flex-grow-1" style="background:#ebe9f1; border-radius:4px; height:6px; overflow:hidden;">
                  <div
                    :style="{
                      width: r.connectionRate + '%',
                      height: '6px',
                      borderRadius: '4px',
                      background: r.connectionRate >= 60 ? '#28c76f' : r.connectionRate >= 30 ? '#ff9f43' : '#ea5455',
                      transition: 'width 0.4s ease',
                    }"
                  />
                </div>
                <span
                  class="font-weight-bold text-nowrap"
                  :style="{ color: r.connectionRate >= 60 ? '#28c76f' : r.connectionRate >= 30 ? '#ff9f43' : '#ea5455', minWidth:'36px' }"
                >{{ r.connectionRate }}%</span>
              </div>
              <span v-else class="text-muted">—</span>
            </td>

            <!-- Not Answered -->
            <td class="px-1 py-75 text-center align-middle">
              <span class="font-weight-bold text-warning">{{ r.notAnswered }}</span>
            </td>

            <!-- Missed (inbound not answered) -->
            <td class="px-1 py-75 text-center align-middle">
              <span class="font-weight-bold" style="color:#00cfe8;">{{ r.missed }}</span>
            </td>

            <!-- Avg Duration -->
            <td class="px-1 py-75 align-middle">
              <span class="text-nowrap font-weight-bold">{{ r.avgDurationFmt }}</span>
            </td>
          </tr>
        </tbody>
        <tfoot style="background:#f8f8f8; border-top:2px solid #ebe9f1;">
          <tr>
            <td class="px-1 py-75 font-weight-bold text-muted">Total</td>
            <td class="px-1 py-75 text-center font-weight-bold">{{ totals.total }}</td>
            <td class="px-1 py-75 text-center font-weight-bold text-success">{{ totals.connected }}</td>
            <td class="px-1 py-75">
              <div class="d-flex align-items-center" style="gap:8px;">
                <div class="flex-grow-1" style="background:#ebe9f1; border-radius:4px; height:6px; overflow:hidden;">
                  <div
                    :style="{
                      width: totals.connectionRate + '%',
                      height: '6px',
                      borderRadius: '4px',
                      background: totals.connectionRate >= 60 ? '#28c76f' : totals.connectionRate >= 30 ? '#ff9f43' : '#ea5455',
                    }"
                  />
                </div>
                <span
                  class="font-weight-bold text-nowrap"
                  :style="{ color: totals.connectionRate >= 60 ? '#28c76f' : totals.connectionRate >= 30 ? '#ff9f43' : '#ea5455', minWidth:'36px' }"
                >{{ totals.connectionRate }}%</span>
              </div>
            </td>
            <td class="px-1 py-75 text-center font-weight-bold text-warning">{{ totals.notAnswered }}</td>
            <td class="px-1 py-75 text-center font-weight-bold" style="color:#00cfe8;">{{ totals.missed }}</td>
            <td class="px-1 py-75 font-weight-bold">{{ totals.avgDurationFmt }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BBadge } from 'bootstrap-vue'

function formatDuration(seconds) {
  const s = parseInt(seconds, 10)
  if (!s || Number.isNaN(s)) return '—'
  const m = Math.floor(s / 60)
  const rem = s % 60
  if (m > 0) return `${m}m ${rem}s`
  return `${rem}s`
}

export default {
  name: 'MyCallsHourlyTable',

  components: {
    BCard,
    BCardHeader,
    BBadge,
  },

  props: {
    hourlyAnalytics: { type: Array, required: true },
  },

  computed: {
    peakTotal() {
      return Math.max(...this.hourlyAnalytics.map(r => r.total), 0)
    },

    enrichedRows() {
      return this.hourlyAnalytics.map(r => ({
        ...r,
        isPeak: r.total > 0 && r.total === this.peakTotal,
        avgDurationFmt: formatDuration(r.avgDuration),
      }))
    },

    totals() {
      const t = this.hourlyAnalytics.reduce(
        (acc, r) => {
          acc.total += r.total
          acc.connected += r.connected
          acc.notAnswered += r.notAnswered
          acc.missed += r.missed
          acc.totalDuration += r.totalDuration
          return acc
        },
        { total: 0, connected: 0, notAnswered: 0, missed: 0, totalDuration: 0 },
      )
      const avgSec = t.connected > 0 ? Math.round(t.totalDuration / t.connected) : 0
      return {
        ...t,
        connectionRate: t.total > 0 ? Math.round((t.connected / t.total) * 100) : 0,
        avgDurationFmt: formatDuration(avgSec),
      }
    },
  },

  methods: {
    isWorkingHour(h) {
      // Work hours: 9:30 AM – 6:30 PM (hour 9 to 18 inclusive)
      return h >= 9 && h <= 18
    },

    rowClass(r) {
      if (r.total === 0) return 'hourly-row--empty'
      if (r.total === this.peakTotal) return 'hourly-row--peak'
      if (!this.isWorkingHour(r.hour)) return 'hourly-row--offhours'
      return ''
    },
  },
}
</script>

<style scoped>
.hourly-table-scroll {
  -webkit-overflow-scrolling: touch;
  overflow-x: auto;
}

@media (max-width: 576px) {
  .hourly-table-scroll td,
  .hourly-table-scroll th {
    white-space: nowrap;
  }
}

.hourly-row--empty td {
  opacity: 0.45;
}

.hourly-row--peak {
  background-color: rgba(255, 159, 67, 0.06) !important;
}

.hourly-row--offhours td:first-child {
  opacity: 0.7;
}
</style>
